import * as React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import {Form, Formik, FormikProps} from 'formik';

import {useHistory, useParams} from 'react-router-dom';

import Modal from 'antd/lib/modal';
import * as Yup from 'yup';

import Helmet from 'react-helmet';

import candleImg from '@app/images/link-icons/candle.svg';
import NavSelectMobile from '@app/components/UI/NavSelectMobile/NavSelectMobile';
import {TesAuthorCredits} from '@app/components/UI/VetTesCard/VetTesCard';
import {MemorialCandle, MemorialsRes, PetMemModal, PetModalProps} from '@app/objects/PetMemorial';
import CustomInput from '@app/components/UI/CustomInput/CustomInput';
import LinkMore from '@app/components/LocalCommon/LinkMore/LinkMore';
import {rest} from '@app/components/Api';
import {convertAllDates, dateConvert} from '@app/components/Pages/PetMemorial/utils/dateConvert';
import {stringBeautifyValidator} from '@app/components/LocalCommon/Utils/Validators';
import PetMemorialGallery from '@app/components/Pages/PetMemorial/PetMemorialModalGallery';
import {bodyOverflowTransform} from '@app/components/LocalCommon/Utils/bodyOverflowTransform';
import listenerForBrowser from '@app/components/Pages/listenerForBrowser';
import FormikField from '@app/components/Forms/CustomFormikField/CustomFormikField';
import {EmailShare, FacebookShare, TwitterShare} from '@app/components/LocalCommon/ShareButtons/ShareButtons';

const validationSchema = Yup.object({
	name: stringBeautifyValidator('Full Name is a required field'),
	email: Yup.string().email('Email must be a valid Email').required('Email is a required field'),
	captcha: Yup.string().required('Please check the box to submit this form'),
});

const PetMemorialModal: React.FC<PetMemModal> = ({
													 modalVisible,
													 setModalVisible,
													 outerModalProps,
													 sent,
													 setSent,
													 ...props
												 }) => {
	const history = useHistory();
	const params: { id?: string | undefined } = useParams();

	const initialModalProps = {
		memorial: {
			petName: '',
			birthday: 0,
			dateCreated: 0,
			dateOfPassing: 0,
			message: '',
			story: '',
			firstName: '',
			lastName: '',
			city: '',
			state: '',
			id: 0,
			images: [],
			testimonial: undefined,
			memorialCandles: []
		}, dateBirth: '', datePass: '', datePost: {month: '', day: 0, year: 0}
	};

	const [modalProps, setModalProps] = React.useState<PetModalProps>(initialModalProps);
	const [isConfirmed, setIsConfirmed] = React.useState<boolean>(false);

	const navItems = [
		{name: 'memory', title: `In loving memory of ${modalProps.memorial && modalProps.memorial.petName}`},
		{name: 'about', title: `${modalProps.memorial && modalProps.memorial.petName}'s Story`},
		{name: 'testimonial', title: 'Vet Testimonial', hidden: !modalProps.memorial.testimonial?.user && true},
	];

	const [currentIndex, setCurrentIndex] = React.useState(0);
	const currentIssue = navItems[currentIndex].name;
	const onSetIssue = (index) => {
		setCurrentIndex(index);
	};

	const [navCollapse, setNavCollapse] = React.useState(false);
	const [sending, setSending] = React.useState(false);
	const candleForm = React.createRef<HTMLDivElement>();

	React.useEffect(() => {
		props.currentMemorialId && history.push(`/pet-memorial/${props.currentMemorialId}`);
	}, [props.currentMemorialId]);

	React.useEffect(() => {
		const id = params.id;
		const outerItem = outerModalProps.filter(item => id && item.id === +id);
		const getMemorialFromList = () => {
			const dates = convertAllDates(outerItem[0].birthday, outerItem[0].dateOfPassing, outerItem[0].dateCreated);
			const memorialFromList = {
				memorial: outerItem[0],
				dateBirth: dates.dateBirth,
				datePass: dates.datePass,
				datePost: dates.datePost
			};
			setModalProps(memorialFromList);
			setModalVisible(true);
		};

		outerItem.length ?
			getMemorialFromList()
			:
			id && rest.get<MemorialsRes>(`v1/memorials/${id}`)
				.then((res) => {
					const dateBirth = dateConvert(res).dateBirth;
					const datePass = dateConvert(res).datePass;
					const datePost = dateConvert(res).datePost;
					setModalProps({memorial: res, dateBirth, datePass, datePost});
					setModalVisible(true);
				});

	}, [params]);

	const onChange = (index) => {
		setCurrentIndex(index);
	};

	const closeModal = () => {
		const id = params.id;
		const outerItem = outerModalProps.filter(item => id && item.id === +id);
		outerItem.length ? history.push('/pet-memorial') :
		history.goBack();
		setModalVisible(false);
		setModalProps(initialModalProps);
	};

	const scrollToCandleForm = () => candleForm.current?.scrollIntoView({behavior: 'smooth'});

	const toDate = (date: number) => {
		const dateObj = new Date(date);

		return dateObj.toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'});
	};

	const sortByDate = (a: MemorialCandle, b: MemorialCandle) => {
		if (a.dateCreated < b.dateCreated) {
			return 1;
		}
		if (a.dateCreated > b.dateCreated) {
			return -1;
		}
		return 0;
	};

	React.useEffect(() => {
		const mql = window.matchMedia('screen and (max-width: 768px)');

		const listenerFunc = () => {
			if (mql.matches) {
				setNavCollapse(true);
			} else {
				setNavCollapse(false);
			}

		};
		listenerFunc();
		listenerForBrowser(mql, listenerFunc);
	}, [currentIndex]);

	React.useEffect(() => {
		setCurrentIndex(0);
	}, [modalProps.memorial.testimonial]);

	React.useEffect(() => {
		setSent(false);
	}, [modalVisible]);
	const [memorialId, setMemorialId] = React.useState(0);

	React.useEffect(() => {
		setMemorialId(modalProps.memorial.id);
	}, [modalProps]);

	return (
		<Formik
			onSubmit={(values, formikActions) => {
				if (isConfirmed) {
					values.memorialId = memorialId;
					setSending(true);
					rest.post('v1/memorials/candle', values)
						.then((response) => {
							setSent(true);
							formikActions.resetForm();
							setSending(false);
						});
				}
			}}
			initialValues={{
				name: '',
				email: '',
				message: '',
				memorialId: memorialId,
				captcha: ''
			}}
			validationSchema={validationSchema}
			render={(formikBag: FormikProps<any>) => {
				return (
					<>
						<Modal
							wrapClassName="pet-mem-modal pet-mem-modal-gallery modal-wrap"
							visible={modalVisible}
							onOk={() => setModalVisible(false)}
							onCancel={() => {
								closeModal();
								formikBag.resetForm();
							}}
							width={1037}
							getContainer="#pet-memorial"
						>
							{
								params.id ?
									<Helmet
										title={params.id ? `In loving memorial of ${modalProps.memorial.petName} | Lap of Love` : 'Pet Memorials | Lap of Love'}
									/>
								: null
							}
							{
								modalProps.memorial.id !== 0 &&
								<div className="pet-mem-modal__content">
									<div className="pet-mem-modal__container">
										<header className="pet-mem-modal__content-header">
											<div className="pet-mem-modal__content-header-title">
												<h4>{modalProps.memorial.petName}</h4>
												<div>
													<span>{modalProps.dateBirth}</span>
													{modalProps.dateBirth && <span> - </span>}
													<span>{modalProps.datePass || '...'}</span>
												</div>
											</div>
											<div className="pet-mem-modal__share-icons header-share-icons">
												<FacebookShare url={`https://www.lapoflove.com/${history.location.pathname}`} />
												<EmailShare url={`https://www.lapoflove.com/${history.location.pathname}`} />
												<TwitterShare url={`https://www.lapoflove.com/${history.location.pathname}`} />
											</div>
											<div className="pet-mem-modal__content-header-nav">
												{modalProps.memorial.memorialCandles?.length > 0 &&
												<div className="pet-mem-modal__content-header-candle">
													<img src={candleImg} alt="candle"/>
													<span>{modalProps.memorial.memorialCandles.length}</span>
												</div>
												}
												<button className="btn btn-sm btn-primary"
														onClick={scrollToCandleForm}
												>

													Light a candle
												</button>
											</div>
										</header>
										<PetMemorialGallery navCollapse={navCollapse} modalProps={modalProps}/>
										{
											navCollapse
												?
												<div className="nav-select-mobile__wrapper">
													<button className="btn btn-primary"
															onClick={scrollToCandleForm}
													>
														Light a candle
													</button>
													<NavSelectMobile
														navItems={navItems}
														onChange={onChange}
														currentIndex={currentIndex}
													/>
													<div className="pet-mem-modal__share-icons wrapper-share-icons">
														<FacebookShare url={`https://www.lapoflove.com/${history.location.pathname}`} />
														<EmailShare url={`https://www.lapoflove.com/${history.location.pathname}`} />
														<TwitterShare url={`https://www.lapoflove.com/${history.location.pathname}`} />
													</div>
												</div>
												:
												<div className="pet-mem-modal__content-nav">
													{navItems && navItems.filter(item => !item.hidden).map((item, index) =>
														<button
															key={index}
															className={currentIndex === index ? 'pet-mem-modal__content-nav-button active' : 'pet-mem-modal__content-nav-button'}

															onClick={() => onSetIssue(index)}
														>
															{item.title}
														</button>)}
												</div>
										}
										<div className="pet-mem-modal__content-issues">
											<div className="pet-mem-modal__content-topic">
												{
													currentIssue === 'memory' ?
														<p>
															{modalProps.memorial && modalProps.memorial.message}
														</p>
														:
														(currentIssue === 'about' ?
																<p>
																	{modalProps.memorial && modalProps.memorial.story}
																</p>
																:
																<div
																	className="pet-mem-modal__content-topic-tes">
																	<div
																		className="pet-mem-modal__content-topic-tes-name">
																		<img
																			src={modalProps.memorial.testimonial?.user?.avatar}
																			alt="vet"/>
																		<span>Dr. {modalProps.memorial.testimonial?.user?.firstName}</span>
																	</div>
																	<p>
																		{modalProps.memorial.testimonial?.message}
																	</p>
																	<LinkMore
																		linkMoreCaption={`View Dr. ${modalProps.memorial.testimonial?.user?.firstName}'s biography`}
																		// function is used directly because using setModalVisible will not fire
																		// the hook while leaving the memorials page
																		handleCLick={() => bodyOverflowTransform(false)}
																		linkMoreLink={`/find-a-vet/${modalProps.memorial.state?.replace(' ', '-')}/${modalProps.memorial.testimonial?.user?.areaUrl}/veterinarians?veterinarian=${modalProps.memorial.testimonial?.user?.id}`}/>
																</div>

														)
												}
											</div>
											<TesAuthorCredits
												firstName={modalProps.memorial.firstName}
												lastName={modalProps.memorial.lastName}
												city={modalProps.memorial.city}
												state={modalProps.memorial.state}
												date={modalProps.datePost}
											/>
										</div>
										<div className="pet-mem-modal__content-comments">
											{
												modalProps.memorial.memorialCandles
													.sort(sortByDate)
													.map((item, index) => {
														const newArray = item.message ? item.message.split('\n') : [];

														return <div key={index}
															className="pet-mem-modal__content-comments-item">
															<div
																className="pet-mem-modal__content-comments-item-main">
																<div>{newArray.map((paragraph, index) =>
																	<p key={index}>{paragraph}</p>)}
																</div>
																<img src={candleImg} alt="candle"/>
															</div>
															<div>
																<span>{item.firstName} {item.lastName}</span>
																<span> - </span>
																<span>{toDate(item.dateCreated)}</span>
															</div>
														</div>;
													})
											}
										</div>
									</div>
								</div>
							}
							<div className="pet-mem-modal__container">
								<div className="pet-mem-modal__content-feedback" ref={candleForm}>

									{
										sent ?
											(<div className="pet-mem-modal__content-feedback__confirmation">
												<h3>Thank you for lighting the candle</h3>
												<h4>
													We will post your message within 5 business days. You and the family
													will receive an email when your candle is ready to view.
												</h4>
											</div>)
											:
											(<>
												<header>
													<h5>{`Light a candle for ${modalProps.memorial.petName}`}</h5>
												</header>
												<Form>
													<div className="pet-mem-modal__content-feedback-inputs">
														<CustomInput
															name="name"
															placeholder="Full Name*"
															formikBag={formikBag}/>
														<CustomInput
															name="email"
															placeholder="Email Address*"
															formikBag={formikBag}
															removeSpace={true}
														/>
													</div>
													<div className="vet-tes-modal__form-textarea">
														<CustomInput
															component={'textarea'}
															name="message"
															placeholder="Your message"
															formikBag={formikBag}
														/>
													</div>
													<FormikField
														containerClassName="captcha"
														fieldName="captcha"
														render={fieldProps =>
															<ReCAPTCHA
																sitekey="6LezTMIcAAAAACaXvLNse5bmTJpwqs0Ezm_bhTrU"
																onChange={(value) => {
																	if (value) {
																		formikBag.setFieldValue('captcha', value);
																		setIsConfirmed(true);
																	} else {
																		formikBag.setFieldValue('captcha', '');
																		setIsConfirmed(false);
																		setSending(false);
																	}
																}}
																onExpired={() => formikBag.setFieldValue('captcha', 'expired')}
																hl="en"
															/>
														}
													/>
													<div className="pet-mem-modal__content-feedback-submit">
														<button
															type="submit"
															className="btn btn-md btn-primary"
															disabled={sending}
														>
															Light your candle
														</button>
														{
															sending ?
																<div className="lds-ring">
																	<div/>
																	<div/>
																	<div/>
																	<div/>
																</div>
																: null
														}
													</div>
												</Form>
											</>)
									}
								</div>
							</div>
						</Modal>
					</>
				);
			}}
		/>

	);
};

export default PetMemorialModal;
